import {
  memo,
  useLayoutEffect,
  useRef,
  useState,
  type FC,
  type MouseEvent,
} from "react";
import {
  estimateTooltipHeight,
  placeTooltip,
  TOOLTIP_MAX_WIDTH_PX,
} from "@/components/tooltipPlacement";
import styles from "@/components/PillBarList.module.css";

interface PillBarTooltipLine {
  // Metric lines ("Impressions: 6,981,961") bold the value; identifying lines
  // ("Name:", "ID:") bold the label — the same split the chart tooltips use.
  isMetric?: boolean;
  label: string;
  value: string;
}

export interface PillBarListRow {
  id?: string;
  label: string;
  // Lines shown in the hover tooltip, e.g. Name / ID / Impressions.
  tooltip?: PillBarTooltipLine[];
  value: number;
}

interface PillBarListProps {
  rows: PillBarListRow[];
  formatValue: (value: number) => string;
}

interface HoverState {
  index: number;
  x: number;
  y: number;
}

const ROW_INDEX_ATTR = "data-row-index"; // set as data-row-index on each cell
// Memoised: the list can hold thousands of rows and the parent re-renders on
// every mouse move to track the tooltip, which must not re-render the rows.
const PillBarRows = memo(({ formatValue, rows }: PillBarListProps) => {
  const maxValue = rows.reduce((max, { value }) => Math.max(max, value), 0);
  return (
    <>
      {rows.map(({ id, label, value }, index) => (
        // Names can repeat across creatives, so the position is part of the key.
        // eslint-disable-next-line react/no-array-index-key
        <li key={`${id ?? label}-${index}`} className={styles.row}>
          <span className={styles.label} data-row-index={index}>
            {label}
          </span>
          <span className={styles.track} data-row-index={index}>
            {value > 0 && maxValue > 0 && (
              <span
                className={styles.bar}
                data-testid="pill-bar"
                style={{ width: `${(value / maxValue) * 100}%` }}
              />
            )}
          </span>
          <span className={styles.value} data-row-index={index}>
            {formatValue(value)}
          </span>
        </li>
      ))}
    </>
  );
});

const PillBarList: FC<PillBarListProps> = ({ formatValue, rows }) => {
  const [hover, setHover] = useState<HoverState | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  // The tooltip's real height once rendered — long names wrap, so the
  // line-count estimate is only good for the first frame.
  const [tooltipHeight, setTooltipHeight] = useState(0);

  // Rows are `display: contents`, so pointer events are read at the list level
  // and mapped back to a row through the cells' data attribute.
  const handleMove = (event: MouseEvent<HTMLUListElement>) => {
    const cell = (event.target as HTMLElement).closest(`[${ROW_INDEX_ATTR}]`);
    const index = cell ? Number(cell.getAttribute(ROW_INDEX_ATTR)) : null;
    setHover(
      index === null ? null : { index, x: event.clientX, y: event.clientY }
    );
  };

  const lines = hover ? rows[hover.index]?.tooltip : undefined;

  useLayoutEffect(() => {
    setTooltipHeight(tooltipRef.current?.offsetHeight ?? 0);
  }, [hover?.index]);

  return (
    <>
      {/* The list handles pointer events only to show a tooltip — it isn't an
          interactive control, so no keyboard handler is needed. */}
      {}
      <ul
        className={styles.list}
        onMouseLeave={() => setHover(null)}
        onMouseMove={handleMove}
      >
        <PillBarRows formatValue={formatValue} rows={rows} />
      </ul>
      {hover && lines && (
        <div
          ref={tooltipRef}
          className={styles.tooltip}
          role="tooltip"
          style={placeTooltip({
            height: tooltipHeight || estimateTooltipHeight(lines.length),
            viewportHeight: window.innerHeight,
            viewportWidth: window.innerWidth,
            width: TOOLTIP_MAX_WIDTH_PX,
            x: hover.x,
            y: hover.y,
          })}
        >
          {lines.map(({ isMetric, label, value }) => (
            <div
              key={label}
              className={isMetric ? styles.metric : undefined}
              data-metric={isMetric ? "true" : undefined}
            >
              <span className={styles.tooltipLabel}>{label}:</span>{" "}
              <span className={styles.tooltipValue}>{value}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default PillBarList;
